import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { Form as ToolkitForm, Icon } from '@contently/toolkit';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';

import SelectField from './SelectField';
import HiddenField from './HiddenField';
import useFetchingField from '../utils/hooks/useFetchingField';
import useFetchingForm from '../utils/hooks/useFetchingForm';
import useLocalStorage from '../utils/hooks/useLocalStorage';
import GA from '../utils/GoogleAnalytics';

const StyledButton = styled(ToolkitForm.Button)`
  margin-right: 1em;
  border: 0;
  background: #e8f7fa;
  font-size: 1rem;
  width: 2em;
  align-self: center;
`;

const ResultsDisplayText = styled('div')`
  display: flex;
  white-space: nowrap;
  align-items: center;
  color: #7a7a7a;
`;

const SearchForm = styled('form')`
  display: flex;
  font-size: 0.9375rem;
  flex-wrap: wrap;
  > * {
    margin: 0 0.625em 0.625em 0;
  }
`;

const SelectWrapper = styled('div')`
  position: relative;
  width: 200px;
`;

const parseTopics = res =>
  res.data.map(topic => {
    return { value: topic.name, label: topic.name };
  });
const parseSkills = res =>
  res.data.map(skill => {
    return { value: skill.group, label: skill.group };
  });

const Search = props => {
  const { count, searchesRemaining, history } = props;
  const [skillsOptions, skillsFetchingState] = useFetchingField(
    '/recommended_contributors/skill_groups',
    [],
    parseSkills
  );
  const [topicsOptions, topicsFetchingState] = useFetchingField(
    '/topics?visible=true',
    [],
    parseTopics
  );
  const [, formErrored] = useFetchingForm(topicsFetchingState, skillsFetchingState);
  const [values, setValues] = useLocalStorage('formValues', {});

  const { skill, topics, email } = values;

  if (formErrored) {
    history.push({
      pathname: '/',
      state: { error: true }
    });
  }

  return (
    <Form
      initialValues={{ email, skill, topics }}
      onSubmit={searchValues => {
        if (GA.trackingEnabled) {
          GA.client.event({
            category: 'User',
            action: 'Additional search',
            label: `${values.skill}/${values.topics}`
          });
        }
        setValues({ ...values, ...searchValues });
      }}
      render={({ handleSubmit }) => (
        <>
          <SearchForm
            action={window.env.LEAD_FORM_ACTION}
            method={window.env.LEAD_FORM_METHOD}
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
              e.target.submit();
            }}
          >
            <ResultsDisplayText>Displaying {count}</ResultsDisplayText>
            <SelectWrapper>
              <SelectField
                name="skill"
                fetching={skillsFetchingState.fetching}
                options={skillsOptions}
                withLabel={false}
              />
            </SelectWrapper>
            <ResultsDisplayText>with professional experience in</ResultsDisplayText>
            <SelectWrapper>
              <SelectField
                name="topics"
                fetching={topicsFetchingState.fetching}
                options={topicsOptions}
                withLabel={false}
              />
            </SelectWrapper>
            <StyledButton
              type="submit"
              variant="secondary"
              size="small"
              disabled={searchesRemaining === 0}
            >
              <Icon type="refresh" />
            </StyledButton>
            <HiddenField name="email" />
          </SearchForm>
        </>
      )}
    />
  );
};

Search.propTypes = {
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchesRemaining: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

export default withRouter(Search);
